/**
 * Authentication Service
 * 
 * Handles user authentication via Supabase Auth.
 * Supports email/password, magic link, and OAuth providers.
 */

import { supabase } from '../lib/supabase';
import type { User, Session, AuthError } from '@supabase/supabase-js';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: AuthError | null;
}

class AuthService {
  private listeners: Set<(state: AuthState) => void> = new Set();
  private currentState: AuthState = {
    user: null,
    session: null,
    loading: true,
    error: null,
  };

  constructor() {
    this.initialize();
  }

  /**
   * Initialise auth state.
   *
   * Two important details, both forced by ugly past bugs:
   *
   * 1. Register `onAuthStateChange` BEFORE calling `getSession()`. The Supabase
   *    client (created at module load with `detectSessionInUrl: true`) parses
   *    the OAuth `#access_token=...` fragment synchronously when the module
   *    is imported, and immediately fires SIGNED_IN. If we await getSession()
   *    first, the listener doesn't exist yet and we miss the event entirely —
   *    the user ends up signed in inside Supabase but our state shows them as
   *    logged out, and HomeRoute renders LandingPage instead of Dashboard.
   *    Reordering means the listener catches both initial events AND any
   *    later auto-refresh ticks regardless of when getSession() resolves.
   *
   * 2. Self-heal stale localStorage. If a previous tab left an expired
   *    refresh token in `sb-*` keys, getSession() returns either an error or
   *    null without cleaning up. Subsequent attempts (auto-refresh) keep
   *    failing silently. Detect any error path and call `signOut({ scope:
   *    'local' })` to nuke the local entries and land in a clean
   *    unauthenticated state. The next sign-in works without the user having
   *    to manually clear DevTools → Local Storage. Was the bug that forced
   *    "open in incognito" workarounds during dev.
   */
  private async initialize() {
    // (1) Register listener FIRST so we don't miss SIGNED_IN events that
    // fire during the synchronous URL-fragment parse on module load.
    supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AuthService] Auth state changed:', event, session ? 'has session' : 'no session');
      this.updateState({
        user: session?.user ?? null,
        session,
        loading: false,
        error: null,
      });
    });

    // (2) Then ask for the current session as a backstop. If the listener
    // already fired with the post-OAuth session, this is redundant but
    // harmless. If we're cold-loading without a redirect, this is the only
    // way to populate the initial state.
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('[AuthService] getSession() returned error, self-healing:', error.message);
        await this.selfHealStaleSession();
        return;
      }

      // No error — set state from whatever getSession returned (may be null,
      // which is fine and means "not signed in").
      this.updateState({
        user: session?.user ?? null,
        session,
        loading: false,
        error: null,
      });
    } catch (err: any) {
      // Hard failure (network, malformed local storage, etc). Same recovery
      // path: nuke local state and go unauthenticated.
      console.error('[AuthService] Failed to initialize, self-healing:', err?.message || err);
      await this.selfHealStaleSession();
    }
  }

  /**
   * Recover from a corrupted or expired local session.
   *
   * Calls `signOut({ scope: 'local' })` which removes the `sb-*` localStorage
   * entries WITHOUT hitting the Supabase server (so it works even when the
   * refresh token is dead). Lands the app in a clean unauthenticated state.
   *
   * Catches all errors — this is best-effort cleanup, must never throw.
   */
  private async selfHealStaleSession(): Promise<void> {
    try {
      await supabase.auth.signOut({ scope: 'local' });
      console.warn('[AuthService] Cleared stale local session — user can now sign in fresh');
    } catch (cleanupErr) {
      console.error('[AuthService] selfHealStaleSession failed:', cleanupErr);
    }
    // Always land in clean unauthenticated state regardless of cleanup outcome
    this.updateState({
      user: null,
      session: null,
      loading: false,
      error: null,
    });
  }

  private updateState(partial: Partial<AuthState>) {
    this.currentState = { ...this.currentState, ...partial };
    this.listeners.forEach((listener) => listener(this.currentState));
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.currentState);
    // Return unsubscribe function
    return () => this.listeners.delete(listener);
  }

  /**
   * Get current auth state
   */
  getState(): AuthState {
    return this.currentState;
  }

  /**
   * Get current user
   */
  getUser(): User | null {
    return this.currentState.user;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.currentState.user;
  }

  /**
   * Sign up with email and password
   * @param captchaToken Optional Cloudflare Turnstile token for bot protection
   */
  async signUp(email: string, password: string, captchaToken?: string): Promise<{ user: User | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });

    if (error) {
      console.error('[AuthService] Sign up error:', error);
      return { user: null, error };
    }

    // Supabase email enumeration protection: when an account already exists
    // (e.g. signed up via Google), it returns a fake success with empty identities
    // instead of an error. Detect this and surface a meaningful message.
    if (data.user && data.user.identities && data.user.identities.length === 0) {
      const conflictError = {
        message: 'An account with this email already exists. Try signing in with Google instead.',
        name: 'AuthApiError',
        status: 400,
      } as unknown as AuthError;
      console.warn('[AuthService] Sign up conflict: email already registered via OAuth');
      return { user: null, error: conflictError };
    }

    return { user: data.user, error: null };
  }

  /**
   * Resend email confirmation link
   */
  async resendConfirmation(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });

    if (error) {
      console.error('[AuthService] Resend confirmation error:', error);
    }

    return { error };
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<{ user: User | null; error: AuthError | null }> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('[AuthService] Sign in error:', error);
      return { user: null, error };
    }

    return { user: data.user, error: null };
  }

  /**
   * Sign in with magic link (passwordless)
   */
  async signInWithMagicLink(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });

    if (error) {
      console.error('[AuthService] Magic link error:', error);
    }

    return { error };
  }

  /**
   * Sign in with OAuth provider (Google, GitHub, etc.)
   */
  async signInWithProvider(provider: 'google' | 'github'): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) {
      console.error('[AuthService] OAuth error:', error);
    }

    return { error };
  }

  /**
   * Sign out
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error('[AuthService] Sign out error:', error);
    }

    return { error };
  }

  /**
   * Reset password
   */
  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error('[AuthService] Reset password error:', error);
    }

    return { error };
  }

  /**
   * Update password (for authenticated users)
   */
  async updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      console.error('[AuthService] Update password error:', error);
    }

    return { error };
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
